import {
  ProtectedRoutes,
  StartParkingOnboardingRoutes,
  TaskItemRoutes,
} from 'apps/customer/routes/paths';

import { OnboardingStep } from './OnboardingStep';
import { stepOrderFor } from './stepOrder';

import type { OnboardingVariant } from '../deriveVariant';
import type { SubscriptionOnboardingVariant } from './onboardingSession';

export type FlowNextAction =
  | {
      path: string;
      resumeOnboardingAfterTasks?: SubscriptionOnboardingVariant;
      type: 'external';
    }
  | { replace?: boolean; step: OnboardingStep; type: 'step' }
  | { type: 'done' };

export type SubscriptionProgressAction = Extract<
  FlowNextAction,
  { type: 'done' } | { type: 'step' }
>;

export type VerifiedExistingAccountContext = {
  email: string | null | undefined;
  hasActiveVisit: boolean;
  hasDefaultPayment: boolean;
  hasName: boolean;
  hasPaymentTask: boolean;
  hasRequiredTasks: boolean;
  hasVehicle: boolean;
  missedEntryStartVisitOnly: boolean;
  promptUserSignIn: boolean;
  signInLandingPage: string | null;
  variant: OnboardingVariant;
};

export type AfterVerifyPhoneContext =
  | { isNewAccount: true; variant: OnboardingVariant }
  | (VerifiedExistingAccountContext & { isNewAccount: false });

export type ResumedOnboardingContext = VerifiedExistingAccountContext & {
  isNewAccount: boolean;
};

export function resolveAfterVerifyPhone(ctx: AfterVerifyPhoneContext): FlowNextAction {
  if (ctx.isNewAccount) {
    return {
      type: 'step',
      step: stepOrderFor({ isNewAccount: true, variant: ctx.variant })[0],
    };
  }

  return resolveVerifiedExistingAccount(ctx);
}

export function resolveResumedOnboarding(ctx: ResumedOnboardingContext): FlowNextAction {
  if (!ctx.isNewAccount) return resolveVerifiedExistingAccount(ctx);

  if (ctx.variant === 'enterpriseSub' || ctx.variant === 'individualSub') {
    return resolveSubscriptionProgress({
      email: ctx.email,
      hasDefaultPayment: ctx.hasDefaultPayment,
      hasName: ctx.hasName,
      hasVehicle: ctx.hasVehicle,
      variant: ctx.variant,
    });
  }

  if (!ctx.hasVehicle) return { type: 'step', step: OnboardingStep.AddVehicle };
  if (!ctx.hasDefaultPayment) return { type: 'step', step: OnboardingStep.AddPayment };
  return ctx.email ? { type: 'done' } : { type: 'step', step: OnboardingStep.AddEmail };
}

function requiredTaskAction(
  path: string,
  variant: OnboardingVariant,
): Extract<FlowNextAction, { type: 'external' }> {
  if (variant === 'enterpriseSub' || variant === 'individualSub') {
    return { path, resumeOnboardingAfterTasks: variant, type: 'external' };
  }
  return { path, type: 'external' };
}

function resolveVerifiedExistingAccount(ctx: VerifiedExistingAccountContext): FlowNextAction {
  if (ctx.hasPaymentTask) return requiredTaskAction(TaskItemRoutes.Tasks, ctx.variant);

  if (ctx.hasRequiredTasks) {
    return ctx.missedEntryStartVisitOnly
      ? requiredTaskAction(ProtectedRoutes.ConfirmMissedEntryPage, ctx.variant)
      : requiredTaskAction(StartParkingOnboardingRoutes.Onboard, ctx.variant);
  }

  if (ctx.variant === 'enterpriseSub' || ctx.variant === 'individualSub') {
    return resolveSubscriptionProgress({
      email: ctx.email,
      hasDefaultPayment: ctx.hasDefaultPayment,
      hasName: ctx.hasName,
      hasVehicle: ctx.hasVehicle,
      variant: ctx.variant,
    });
  }

  if (ctx.promptUserSignIn) {
    if (!ctx.email) return { type: 'step', step: OnboardingStep.AddEmail };
    if (!ctx.hasVehicle) return { type: 'step', step: OnboardingStep.AddVehicle };
    if (!ctx.hasDefaultPayment) return { type: 'step', step: OnboardingStep.AddPayment };
    return { type: 'external', path: ctx.signInLandingPage || ProtectedRoutes.ForwardSlash };
  }

  if (ctx.signInLandingPage && ctx.signInLandingPage !== ProtectedRoutes.HomePage) {
    return { type: 'external', path: ctx.signInLandingPage };
  }
  if (ctx.hasActiveVisit) return { type: 'external', path: ProtectedRoutes.VisitPage };

  return {
    type: 'external',
    path:
      ctx.signInLandingPage === ProtectedRoutes.HomePage
        ? ProtectedRoutes.HomePage
        : ProtectedRoutes.DashboardPage,
  };
}

export function resolveSubscriptionProgress({
  email,
  hasDefaultPayment,
  hasName,
  hasVehicle,
  variant,
}: {
  email: string | null | undefined;
  hasDefaultPayment: boolean;
  hasName: boolean;
  hasVehicle: boolean;
  variant: 'enterpriseSub' | 'individualSub';
}): SubscriptionProgressAction {
  if (!email || (variant === 'enterpriseSub' && !hasName)) {
    return { type: 'step', step: OnboardingStep.AddEmail };
  }
  if (!hasVehicle) return { type: 'step', step: OnboardingStep.AddVehicle };
  if (variant === 'enterpriseSub') return { type: 'done' };
  if (!hasDefaultPayment) return { type: 'step', step: OnboardingStep.AddPayment };
  return { type: 'step', step: OnboardingStep.ReviewSubscription };
}

export type ShouldShowAddVehiclePageContext = {
  openVisitVehicleId: number | undefined;
  ownedVehicleIds: number[];
  sharedVehicleRoleByVehicleId: Record<number, string | undefined>;
  visitOpen: boolean;
};

export function shouldShowAddVehiclePage(ctx: ShouldShowAddVehiclePageContext): boolean {
  if (!ctx.visitOpen || ctx.openVisitVehicleId === undefined) return true;
  if (!ctx.ownedVehicleIds.includes(ctx.openVisitVehicleId)) return true;

  const role = ctx.sharedVehicleRoleByVehicleId[ctx.openVisitVehicleId];
  return role === undefined || role === 'Primary';
}

export type AfterAddEmailContext = {
  contactCollectedLast: boolean;
  hasDefaultPayment: boolean;
  hasRequiredTasks: boolean;
  hasVehicle: boolean;
  isInvitedUser: boolean;
  showAddVehiclePage: boolean;
  variant: OnboardingVariant;
};

export function resolveAfterAddEmail(ctx: AfterAddEmailContext): FlowNextAction {
  if (ctx.variant === 'enterpriseSub') {
    return !ctx.hasVehicle ? { type: 'step', step: OnboardingStep.AddVehicle } : { type: 'done' };
  }

  if (ctx.variant === 'individualSub') {
    if (!ctx.hasVehicle) return { type: 'step', step: OnboardingStep.AddVehicle };
    if (!ctx.hasDefaultPayment) return { type: 'step', step: OnboardingStep.AddPayment };
    return { type: 'step', step: OnboardingStep.ReviewSubscription };
  }

  if (ctx.contactCollectedLast) return { type: 'done' };

  if (ctx.hasRequiredTasks && !ctx.isInvitedUser) {
    return { type: 'external', path: TaskItemRoutes.Tasks };
  }

  return {
    type: 'step',
    step: ctx.showAddVehiclePage ? OnboardingStep.AddVehicle : OnboardingStep.AddPayment,
  };
}

export type AfterAddVehicleContext = {
  hasDefaultPayment: boolean;
  hasPaymentTask: boolean;
  isVisitOpen: boolean;
  signInLandingPage: string | null;
  variant: OnboardingVariant;
};

export function resolveAfterAddVehicle(ctx: AfterAddVehicleContext): FlowNextAction {
  if (ctx.variant === 'enterpriseSub') return { type: 'done' };
  if (ctx.hasPaymentTask) return requiredTaskAction(TaskItemRoutes.Tasks, ctx.variant);
  if (!ctx.hasDefaultPayment) return { type: 'step', step: OnboardingStep.AddPayment };
  if (ctx.variant === 'individualSub') {
    return { type: 'step', step: OnboardingStep.ReviewSubscription };
  }
  if (ctx.signInLandingPage && ctx.signInLandingPage !== ProtectedRoutes.HomePage) {
    return { type: 'external', path: ctx.signInLandingPage };
  }

  return {
    type: 'external',
    path: ctx.isVisitOpen ? ProtectedRoutes.VisitPage : ProtectedRoutes.DashboardPage,
  };
}

export type AfterAddPaymentContext = {
  contactCollectedLast: boolean;
  email: string | null | undefined;
  variant: OnboardingVariant;
};

export function resolveAfterAddPayment(ctx: AfterAddPaymentContext): FlowNextAction {
  if (ctx.variant === 'individualSub') {
    return { type: 'step', step: OnboardingStep.ReviewSubscription };
  }
  if (ctx.contactCollectedLast && !ctx.email) {
    return { type: 'step', step: OnboardingStep.AddEmail };
  }
  return { type: 'done' };
}
